import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Global 401 handler: when the session cookie is missing/expired, send the user
// back to the admin login page once (per page load). The /auth/me and /auth/login
// probes are excluded — AuthContext expects those 401s to flow through.
let redirecting = false;
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthProbe = url.endsWith("/auth/me") || url.endsWith("/auth/login");
    if (status === 401 && !isAuthProbe && !redirecting) {
      const path = window.location.pathname;
      const alreadyOnLogin = path === "/admin/login" || path.startsWith("/admin/login");
      const isProtected = path.startsWith("/admin") || path.startsWith("/practitioner");
      if (isProtected && !alreadyOnLogin) {
        redirecting = true;
        window.location.replace(`/admin/login?next=${encodeURIComponent(path)}`);
      }
    }
    return Promise.reject(err);
  },
);

export default api;

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  return String(detail);
}

export function formatPrice(dollars) {
  if (dollars == null) return "—";
  const n = Number(dollars);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

// Practice timezone — all wall-clock display and day boundaries use this.
export const PRACTICE_TZ = "America/Chicago";

// Returns "YYYY-MM-DD" of the given Date in the practice timezone.
export function tzDateKey(date) {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PRACTICE_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).format(d);
}

// Given a "YYYY-MM-DD" (interpreted in practice TZ), returns a Date representing
// midnight on that calendar day in the practice timezone (as an absolute moment).
export function tzMidnight(ymd) {
  // Probe the UTC↔practice-TZ offset for the given date
  const probe = new Date(`${ymd}T12:00:00Z`);
  const ctHourStr = new Intl.DateTimeFormat("en-US", {
    timeZone: PRACTICE_TZ, hour: "numeric", hour12: false,
  }).format(probe);
  const ctHour = parseInt(ctHourStr, 10) % 24;
  // CT hour at UTC 12:00 → offset hours that UTC midnight is BEHIND CT midnight
  const offsetHours = (12 - ctHour + 24) % 24;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, offsetHours, 0, 0));
}

// Returns the hour (0-23) of `date` in the practice timezone.
export function tzHours(date) {
  return parseInt(new Intl.DateTimeFormat("en-US", {
    timeZone: PRACTICE_TZ, hour: "numeric", hour12: false,
  }).format(date), 10) % 24;
}

// Returns the minute (0-59) of `date` in the practice timezone.
export function tzMinutes(date) {
  return parseInt(new Intl.DateTimeFormat("en-US", {
    timeZone: PRACTICE_TZ, minute: "2-digit",
  }).format(date), 10);
}

// Returns the weekday number (0=Sun..6=Sat) of `date` in the practice timezone.
export function tzWeekday(date) {
  const wd = new Intl.DateTimeFormat("en-US", {
    timeZone: PRACTICE_TZ, weekday: "short",
  }).format(date);
  return ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].indexOf(wd);
}

export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", timeZone: PRACTICE_TZ });
}

export function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ });
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: PRACTICE_TZ })} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZone: PRACTICE_TZ })}`;
}
